// Find RICs, the bulk runner (spec 5.1, 5.4, 5.6, 5.7).
//
// A selected column of identifiers and names -> column B gets the RIC and
// column C the correspondence, one row at a time, written in blocks as rows
// finish. The runner talks to the sheet only through SheetIO, so it is tested
// against a fake; excelSheetIO is the Office.js implementation.
//
// Rows and columns are 0-based (as in Excel's getRangeByIndexes). The input
// column is `col`; B and C are col + 1 and col + 2.
//
// What a run guarantees:
//   - Only the sheet and addresses captured at the start are written, never the
//     active sheet. The run goes on while the pane is closed.
//   - Before each block of 20 rows it reads the rows again: a row whose A
//     changed, or whose B/C differ from what the run saw when it planned (a
//     user filled them), is skipped and counted, never overwritten.
//   - Hidden (filtered-out) and empty rows are untouched.
//   - Cancel (the signal) ends the run after the current row and keeps what
//     was written; so does a signed-out add-in (`stopped: "signed-out"`).

import { literalOf, markOwn } from "../lib/excelPort";
import type { WriteCell } from "../lib/writer";
import { formatC, NO_RESULT, PARTIAL, UNAVAILABLE } from "./columnC";
import { isSignedOut, resolveRow, type Resolution } from "./resolve";

/** Resolved rows written per block. */
export const BLOCK_SIZE = 20;
/** The header row's cells (spec 5.1). */
export const HEADER_B = "RIC";
export const HEADER_C = "Correspondance";
/** A trimmed text outside this length is not searched (ruling 7). */
export const MIN_LEN = 2;
export const MAX_LEN = 64;

export interface SheetIO {
  /** The display text (`Range.text`) and row visibility of `count` cells of column `col`, from `firstRow`. */
  readRows(sheet: string, firstRow: number, col: number, count: number): Promise<{ text: string; hidden: boolean }[]>;
  /** Display text of B and C (columns col + 1, col + 2) of each of `rows`. */
  readBC(sheet: string, rows: number[], col: number): Promise<{ b: string; c: string }[]>;
  /** Values only. */
  write(sheet: string, cells: { row: number; col: number; value: string }[]): Promise<void>;
}

export interface RowInput {
  text: string;
  hidden: boolean;
}
export interface RowExisting {
  b: string;
  c: string;
}

export interface Plan {
  /** Index (into the input) of the header row, or null. */
  header: number | null;
  /** Indexes of the rows to resolve, in order. */
  rows: number[];
  /** B/C cells that are not empty and would be overwritten: the confirmation's figure. */
  nonEmptyTargets: number;
}

const HEADER_RE = /^(isin|sedol|cusip|ric|code|ticker|name|nom|libellé|identifiant)s?$/i;

const blank = (s: string): boolean => s.trim() === "";

/** B/C of row i as read; a missing entry is empty. */
const bcAt = (existing: RowExisting[], i: number): RowExisting => existing[i] ?? { b: "", c: "" };

/** Whether a row is asked again by a re-run (spec 5.6). */
function wantsRerun(e: RowExisting): boolean {
  return blank(e.b) || e.c.includes(UNAVAILABLE) || e.c.includes(PARTIAL);
}

/** The header cells a run writes: only those that do not already say it. */
function headerCells(e: RowExisting): { b?: string; c?: string } {
  const out: { b?: string; c?: string } = {};
  if (e.b !== HEADER_B) out.b = HEADER_B;
  if (e.c !== HEADER_C) out.c = HEADER_C;
  return out;
}

/**
 * Which rows a run resolves. `input` and `existing` are aligned by row. The
 * first non-empty visible cell is a header when it is a word like « ISIN »,
 * « Code » or « Name » (5.1); hidden and empty rows are skipped; unless
 * `redoAll`, rows that already have a RIC are kept (5.6).
 */
export function planRun(input: RowInput[], existing: RowExisting[], redoAll: boolean): Plan {
  const at = (i: number): RowExisting => bcAt(existing, i);
  const plan: Plan = { header: null, rows: [], nonEmptyTargets: 0 };
  let first = true;
  for (let i = 0; i < input.length; i++) {
    const { text, hidden } = input[i];
    const t = text.trim();
    if (hidden || t === "") continue;
    if (first) {
      first = false;
      if (HEADER_RE.test(t)) {
        plan.header = i;
        const h = headerCells(at(i));
        if (h.b !== undefined && !blank(at(i).b)) plan.nonEmptyTargets++;
        if (h.c !== undefined && !blank(at(i).c)) plan.nonEmptyTargets++;
        continue;
      }
    }
    const e = at(i);
    if (!redoAll && !wantsRerun(e)) continue;
    plan.rows.push(i);
    if (!blank(e.b)) plan.nonEmptyTargets++;
    if (!blank(e.c)) plan.nonEmptyTargets++;
  }
  return plan;
}

export interface RunTarget {
  sheet: string;
  /** First row of the selection (0-based). */
  firstRow: number;
  /** The input column: the first column of the selection (0-based). */
  col: number;
  count: number;
}

export interface RunOptions {
  redoAll: boolean;
  onProgress?(done: number, total: number): void;
  signal?: AbortSignal;
  resolve?: (text: string) => Promise<Resolution>;
  /**
   * Asked once, after the run is planned and before anything is resolved or
   * written, when the plan would overwrite non-empty B/C cells (spec 5.4: one
   * confirmation for the whole run, naming how many). Declined -> nothing is
   * written and the result is `cancelled`. Only the cells counted in that plan
   * may be overwritten: a cell that changes afterwards is skipped (5.7).
   */
  confirm?(nonEmptyTargets: number): Promise<boolean>;
}

export interface RunResult {
  /** Rows written with a RIC. */
  written: number;
  /** Rows skipped because the sheet changed under the run (5.7). */
  skippedEdited: number;
  /** Rows written « Aucun résultat ». */
  none: number;
  /** Rows written « Recherche indisponible ». */
  unavailable: number;
  cancelled: boolean;
  /** Set when the run ended early for another reason: the add-in was signed out, or the user declined the confirmation (then `cancelled` is true too). */
  stopped?: "signed-out" | "declined";
}

type Kind = "ric" | "none" | "unavailable" | "header";

interface Pending {
  /** Index in the selection. */
  idx: number;
  /** The input as read (trimmed) when the run planned. */
  text: string;
  /** B and C as the run saw them when it planned. */
  base: RowExisting;
  kind: Kind;
  b?: string;
  c?: string;
}

export async function runFindRics(io: SheetIO, target: RunTarget, opts: RunOptions): Promise<RunResult> {
  const { sheet, firstRow, col, count } = target;
  const resolve = opts.resolve ?? ((t: string) => resolveRow(t));
  const result: RunResult = { written: 0, skippedEdited: 0, none: 0, unavailable: 0, cancelled: false };

  const input = await io.readRows(sheet, firstRow, col, count);
  const existing = await io.readBC(sheet, input.map((_, i) => firstRow + i), col);
  const plan = planRun(input, existing, opts.redoAll);
  const total = plan.rows.length;
  if (opts.confirm && plan.nonEmptyTargets > 0 && !(await opts.confirm(plan.nonEmptyTargets))) {
    result.cancelled = true;
    result.stopped = "declined";
    return result;
  }
  const baseOf = (i: number): RowExisting => bcAt(existing, i);

  let pending: Pending[] = [];
  if (plan.header !== null) {
    const h = headerCells(baseOf(plan.header));
    if (h.b !== undefined || h.c !== undefined) {
      pending.push({
        idx: plan.header,
        text: input[plan.header].text.trim(),
        base: baseOf(plan.header),
        kind: "header",
        b: h.b,
        c: h.c,
      });
    }
  }

  // Re-read the block's rows, drop those the user touched, write the rest.
  const flush = async (): Promise<void> => {
    if (!pending.length) return;
    const items = pending;
    pending = [];
    const lo = Math.min(...items.map((p) => p.idx));
    const hi = Math.max(...items.map((p) => p.idx));
    const nowA = await io.readRows(sheet, firstRow + lo, col, hi - lo + 1);
    const nowBC = await io.readBC(sheet, items.map((p) => firstRow + p.idx), col);
    const cells: { row: number; col: number; value: string }[] = [];
    items.forEach((p, j) => {
      const a = nowA[p.idx - lo];
      const bc = nowBC[j];
      if (!a || !bc || a.text.trim() !== p.text || bc.b !== p.base.b || bc.c !== p.base.c) {
        result.skippedEdited++;
        return;
      }
      const row = firstRow + p.idx;
      if (p.b !== undefined) cells.push({ row, col: col + 1, value: p.b });
      if (p.c !== undefined) cells.push({ row, col: col + 2, value: p.c });
      if (p.kind === "ric") result.written++;
      else if (p.kind === "none") result.none++;
      else if (p.kind === "unavailable") result.unavailable++;
    });
    if (cells.length) await io.write(sheet, cells);
  };

  opts.onProgress?.(0, total);
  let done = 0;
  try {
    for (const idx of plan.rows) {
      if (opts.signal?.aborted) {
        result.cancelled = true;
        break;
      }
      const text = input[idx].text.trim();
      const base = baseOf(idx);
      let r: Resolution;
      if (text.length < MIN_LEN || text.length > MAX_LEN) r = { status: "none", candidates: [] };
      else r = await resolve(text);

      const kind: Kind = r.status === "ok" && r.ric ? "ric" : r.status === "unavailable" ? "unavailable" : "none";
      // An outage never blanks a RIC the row held when the run planned: B and
      // C are left as they are (the row is still counted as unavailable).
      const keep = kind === "unavailable" && !blank(base.b);
      pending.push({
        idx,
        text,
        base,
        kind,
        b: keep ? undefined : kind === "ric" ? r.ric! : "",
        c: keep ? undefined : kind === "none" ? NO_RESULT : formatC(r),
      });
      opts.onProgress?.(++done, total);
      if (pending.filter((p) => p.kind !== "header").length >= BLOCK_SIZE) await flush();
    }
  } catch (e) {
    if (isSignedOut(e)) result.stopped = "signed-out";
    else {
      await flush();
      throw e;
    }
  }
  await flush();
  return result;
}

// --- Office.js ---------------------------------------------------------------

/** Cells proxied per Excel round trip. */
const CHUNK = 500;

/**
 * The Excel-backed SheetIO. Reads the display text (`Range.text`, so a SEDOL
 * keeps its leading zero) and `rowHidden` (hidden or filtered out); writes
 * values only, with the text prefix on text Excel would parse (as excelPort).
 */
export function excelSheetIO(): SheetIO {
  return {
    readRows: (sheet, firstRow, col, count) =>
      Excel.run(async (context) => {
        const ws = context.workbook.worksheets.getItem(sheet);
        const out: { text: string; hidden: boolean }[] = [];
        for (let start = 0; start < count; start += CHUNK) {
          const n = Math.min(CHUNK, count - start);
          const ranges = Array.from({ length: n }, (_, i) => {
            const r = ws.getRangeByIndexes(firstRow + start + i, col, 1, 1);
            r.load("text,rowHidden");
            return r;
          });
          await context.sync();
          for (const r of ranges) out.push({ text: String(r.text?.[0]?.[0] ?? ""), hidden: r.rowHidden === true });
        }
        return out;
      }),

    readBC: (sheet, rows, col) =>
      Excel.run(async (context) => {
        const ws = context.workbook.worksheets.getItem(sheet);
        const out: { b: string; c: string }[] = [];
        for (let start = 0; start < rows.length; start += CHUNK) {
          const ranges = rows.slice(start, start + CHUNK).map((row) => {
            const r = ws.getRangeByIndexes(row, col + 1, 1, 2);
            r.load("text");
            return r;
          });
          await context.sync();
          for (const r of ranges) out.push({ b: String(r.text?.[0]?.[0] ?? ""), c: String(r.text?.[0]?.[1] ?? "") });
        }
        return out;
      }),

    write: (sheet, cells) =>
      cells.length
        ? Excel.run(async (context) => {
            const ws = context.workbook.worksheets.getItem(sheet);
            for (const c of cells) {
              markOwn({ sheet, row: c.row, col: c.col, rows: 1, cols: 1 });
              ws.getRangeByIndexes(c.row, c.col, 1, 1).values = [[literalOf({ v: c.value } satisfies WriteCell)]];
            }
            await context.sync();
          })
        : Promise.resolve(),
  };
}
